/**
 * Portal onboarding + key-management calls against the developers-portal backend (`apps/api`). The
 * portal API is **single-env**: each env (staging01, production) runs its own deployment, so every call
 * targets a specific env's portal API via {@link portalApiBase} — onboarding provisions the partner in
 * each env with one call per env, and there is no server-side cross-env fan-out. Every call carries the
 * signed-in user's short-lived **access token** as the Bearer credential — the browser never holds the
 * partner api-key. The KYB business entity is then created against the payouts `/v2` API (also with the
 * access token), so onboarding is fully keyless.
 */
import { createEntityWithToken, listBusinessesWithToken } from './payouts';
import { getSonarSessionToken } from '@/lib/sonarSdk';
import { forceRefreshToken, handleSessionExpired } from '@/lib/authBridge';
import { GATE_ENV, portalApiBase, type EnvironmentId } from '@/config/environments';

/** Envs the backend provisions into. Same string values as the client's {@link EnvironmentId}. */
export type OnboardEnv = EnvironmentId;

/** The signed-in user's portal context — onboarding state + partner. */
export type PortalMe = {
  /** True when the user has a partner + business entity in this env. */
  onboarded: boolean;
  partnerName?: string;
  /** The company name the partner registered with, for display (dashboard top bar). */
  companyName?: string;
  /**
   * Whether this partner is cleared to operate (manage api-keys) in this env — an offline grant reported by
   * the deployment's `/me`. The live keys UI gates on the production `/me` value; the production `/keys`
   * routes also enforce it server-side (403 `PARTNER_NOT_OPERATIONAL`). Absent/false until the grant is given.
   */
  operational?: boolean;
};

/** An error from a portal API call, carrying the backend's HTTP status + error code when present. */
export type PortalError = Error & { status?: number; code?: string };

async function portalJson<T>(env: EnvironmentId, path: string, accessToken: string, init: RequestInit = {}): Promise<T> {
  const send = (bearer: string) =>
    fetch(`${portalApiBase(env)}${path}`, { ...init, headers: { Authorization: `Bearer ${bearer}`, ...(init.headers ?? {}) } });

  let res = await send(accessToken);
  // On a 401 the access token is stale: force a single coordinated refresh and retry once. If the
  // refresh fails (refresh token expired), fall through to the error below and start the sign-in flow.
  if (res.status === 401) {
    const refreshed = await forceRefreshToken();
    if (refreshed) res = await send(refreshed);
    else handleSessionExpired();
  }

  const body = (await res.json().catch(() => undefined)) as (T & { error?: { message?: string; code?: string } }) | undefined;
  if (!res.ok) {
    const err = new Error(body?.error?.message || `Request failed (${res.status})`) as PortalError;
    err.status = res.status;
    err.code = body?.error?.code;
    throw err;
  }
  return (body ?? ({} as T)) as T;
}

/**
 * GET /me — the signed-in user's portal context (read from the env's melio-api DB: `sub → partner`
 * mapping + the partner's own-entity flag). The SPA's bootstrap/gate call, answered by the {@link
 * GATE_ENV} portal API by default; onboarding resume passes a specific env.
 */
export async function fetchMe(accessToken: string, signal?: AbortSignal, env: EnvironmentId = GATE_ENV): Promise<PortalMe> {
  return portalJson<PortalMe>(env, '/me', accessToken, { signal });
}

/**
 * The "apply for beta" application sent to `/onboard`. The server vets it (auto-approved for now),
 * persists it for offline review, and then provisions the partner. `email` is the business owner's,
 * collected on the wizard's owner step — the access token carries no email claim (auth-service puts it
 * only in the id_token), so it has to be sent explicitly.
 */
export type PartnerApplication = {
  /** The business owner's email, so a reviewer can reach the applicant. */
  email: string;
  companyName: string;
  website?: string;
  companyAddress?: string;
  industry?: string;
  /** 'own' (their own company) or 'platform' (a platform on behalf of others). */
  buildingFor: string;
  projectDescription: string;
};

/**
 * POST /onboard — submit the beta application; once it passes vetting (auto-approved for now) the server
 * provisions the partner and records the mapping. Responds with `{ ok: true }` — the partner name isn't
 * returned (read it from `/me`), and the api-key stays server-side. Rejects 409 (`PARTNER_ALREADY_EXISTS`)
 * if the user already has a partner, or 403 if the application isn't approved.
 */
export async function onboardPartner(
  env: EnvironmentId,
  accessToken: string,
  application: PartnerApplication,
  signal?: AbortSignal
): Promise<void> {
  await portalJson<{ ok: true }>(env, '/onboard', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(application),
    signal,
  });
}

/**
 * Provisions the partner in one env, then reads its name back from that env's `/me`. Tolerant of a resume:
 * a partner already provisioned here 409s (`PARTNER_ALREADY_EXISTS`), which we swallow — either way the
 * partner exists, so `/me` yields its name for the (idempotent) KYB entity create that follows.
 */
export async function onboardOrExistingPartner(env: EnvironmentId, accessToken: string, application: PartnerApplication): Promise<string> {
  try {
    await onboardPartner(env, accessToken, application);
  } catch (error) {
    const portalError = error as PortalError;
    if (portalError.code !== 'PARTNER_ALREADY_EXISTS' && portalError.status !== 409) throw error;
  }
  const me = await fetchMe(accessToken, undefined, env);
  if (!me.partnerName) throw new Error('Onboarding did not provision a partner in this environment.');
  return me.partnerName;
}

/**
 * Creates the partner's own business entity in one env, if it doesn't already have one
 * (check-before-create: `POST /v2/entities` uses a fresh `externalId` each call, so a blind retry on
 * resume would duplicate it). The entity call is authorized with the access token; the Sonar token is
 * minted from the non-secret `partnerName` — no api-key or publishable key ever enters the browser.
 */
export async function ensureEntityForEnv(
  env: EnvironmentId,
  accessToken: string,
  partnerName: string,
  entityBody: Record<string, unknown>
): Promise<void> {
  const existing = await listBusinessesWithToken(env, accessToken);
  if (existing.length > 0) return;
  const sonarToken = await getSonarSessionToken(env, partnerName);
  await createEntityWithToken(env, accessToken, entityBody, { sonarToken });
}

// ---- API-key management (dashboard) -----------------------------------------------------
/**
 * Key metadata returned by the management API — never the secret value. `sonarKey` is the same
 * non-secret `pk_<env>_…` publishable key for every entry in the list (it's derived from the
 * partner name + env, not the individual key) — safe to hand to the partner's own client-side code
 * to initialize the Sonar SDK.
 */
export type ApiKeyMeta = {
  id: number;
  keyName: string;
  createdAt: string;
  expiredAt: string | null;
  maskedKey: string | null;
  sonarKey: string;
};

/** GET /keys — the caller partner's key metadata for the env. */
export async function listKeys(accessToken: string, env: OnboardEnv): Promise<ApiKeyMeta[]> {
  const { keys } = await portalJson<{ keys: ApiKeyMeta[] }>(env, '/keys', accessToken);
  return keys;
}

/** GET /keys/:id — reveal a single key's full value on demand. */
export async function revealKey(accessToken: string, env: OnboardEnv, id: number): Promise<string> {
  const { apiKey } = await portalJson<{ apiKey: string }>(env, `/keys/${id}`, accessToken);
  return apiKey;
}

/** POST /keys/rotate — regenerate a named key; returns the new value once. */
export async function rotateKey(accessToken: string, env: OnboardEnv, keyName: string): Promise<string> {
  const { apiKey } = await portalJson<{ apiKey: string }>(env, '/keys/rotate', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ keyName }),
  });
  return apiKey;
}

/** POST /keys/:id/expire — revoke a key. */
export async function expireKey(accessToken: string, env: OnboardEnv, id: number): Promise<void> {
  await portalJson(env, `/keys/${id}/expire`, accessToken, { method: 'POST' });
}
